<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '../table';
import { Button } from '../button';
import { Skeleton } from '../skeleton';
import { Input } from '../input';
import { crossAppActions } from '../cross-app-actions';
import { alertModal } from '../modals/AlertModal.vue';
import type { Email, ISO8601Timestamp, UserWithPermissions } from '../../cross-app-shared/types/common';
import type {
  FacilityGroupID,
  FacilityID,
  PermissionID,
  PermissionTypeID,
  TenantID,
  UserID,
} from '../../cross-app-shared/types/db';
import { facilitiesByFacilityID, groupsByGroupID, tenantsByTenantID } from '../facilities';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../sheet';
import { Label } from '../label';

// Import image for visual header
import usersImg from '../../tri-frontend/assets/man.png';
import { jsonClone } from '../../cross-app-shared/services/util';
import { you } from '../users';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase.ts';
import { Checkbox } from '../checkbox';

const loading = ref(true);
const users = ref<UserWithPermissions[]>([]);
const searchQuery = ref('');
const filterType = ref<'all' | 'admins' | 'noPermissions' | 'facility' | 'facilityNonAdmin'>('all');
const filterFacilityID = ref<FacilityID | null>(null);
const userCreateSheetOpen = ref(false);
const userNew = ref({
  name: '',
  email: '',
  sendSetPasswordEmail: false,
  primaryTenantID: you.value.primaryTenantID as TenantID | null,
  primaryFacilityGroupID: null as FacilityGroupID | null,
  primaryFacilityID: null as FacilityID | null,
});
const userEditing = ref<null | UserWithPermissions>(null);
const userPermissionAdding = ref<{
  permissionTypeID: PermissionTypeID;
  tenantID: TenantID | null;
  facilityGroupID: FacilityGroupID | null;
  facilityID: FacilityID | null;
}>({
  permissionTypeID: 'updateFacility',
  tenantID: null,
  facilityGroupID: null,
  facilityID: null,
});
const userEditingSheetOpen = computed({
  get(): boolean {
    return !!userEditing.value;
  },

  set(newVal: boolean) {
    if (!newVal) {
      userEditing.value = null;
    }
  },
});

onMounted(async () => {
  try {
    users.value = await crossAppActions.users.listForManagement();
  } catch (error: any) {
    console.error('Failed to fetch users:', error);
    await alertModal({
      title: 'Failed to fetch users',
      message: `An unexpected error occurred and we were unable to fetch any users. Error was: ${error.message}`,
    });
  } finally {
    loading.value = false;
  }
});

const userPermissionDescriptions = computed((): Record<UserID, string> => {
  const result: Record<UserID, string> = {};

  for (const user of users.value) {
    if (user.permissions.find((p) => p.permissionTypeID === 'manageUsers')) {
      result[user.userID] = 'Admin';
      continue;
    }

    const facilities = user.permissions.filter(
      (p) =>
        (p.permissionTypeID === 'updateFacility' ||
          p.permissionTypeID === 'updateBaseFacts' ||
          p.permissionTypeID === 'administerFacility') &&
        p.facilityID !== null,
    );
    if (facilities.length > 0) {
      if (facilities.length === 1) {
        result[user.userID] = facilitiesByFacilityID.value[facilities[0].facilityID!]?.name ?? '';
      } else {
        result[user.userID] = `${facilities.length} ${facilities.length === 1 ? 'Facility' : 'Facilities'}`;
      }
      continue;
    }

    const groups = user.permissions.filter(
      (p) =>
        (p.permissionTypeID === 'updateFacility' ||
          p.permissionTypeID === 'updateBaseFacts' ||
          p.permissionTypeID === 'administerFacility') &&
        p.facilityGroupID !== null,
    );
    if (groups.length > 0) {
      result[user.userID] = `${groups.length} ${groups.length === 1 ? 'Group' : 'Groups'}`;
      continue;
    }

    const tenants = user.permissions.filter(
      (p) =>
        (p.permissionTypeID === 'updateFacility' ||
          p.permissionTypeID === 'updateBaseFacts' ||
          p.permissionTypeID === 'administerFacility') &&
        p.tenantID !== null,
    );
    if (tenants.length > 0) {
      result[user.userID] = `${tenants.length} ${tenants.length === 1 ? 'Tenant' : 'Tenants'}`;
      continue;
    }

    result[user.userID] = `${user.permissions.length} ${user.permissions.length === 1 ? 'Permission' : 'Permissions'}`;
  }

  return result;
});

const usersSorted = computed(() => {
  return users.value.slice().sort((a, b) => {
    if (a.lastLoginAt && b.lastLoginAt) {
      return b.lastLoginAt.localeCompare(a.lastLoginAt);
    } else if (a.lastLoginAt && !b.lastLoginAt) {
      return -1;
    } else if (!a.lastLoginAt && b.lastLoginAt) {
      return 1;
    }

    return a.email.localeCompare(b.email);
  });
});

const facilitiesSorted = computed(() => {
  return Object.values(facilitiesByFacilityID.value).sort((a, b) => a.name.localeCompare(b.name));
});

function userCanAccessFacility(user: UserWithPermissions, facilityID: FacilityID): boolean {
  const facility = facilitiesByFacilityID.value[facilityID];
  if (!facility) {
    return false;
  }
  return user.permissions.some((p) => {
    if (p.facilityID !== null) {
      return p.facilityID === facilityID;
    }
    if (p.facilityGroupID !== null) {
      return p.facilityGroupID === facility.facilityGroupID;
    }
    if (p.tenantID !== null) {
      return p.tenantID === facility.tenantID;
    }
    return true;
  });
}

const usersFiltered = computed(() => {
  let result = usersSorted.value;

  if (filterType.value === 'admins') {
    result = result.filter((u) => u.permissions.some((p) => p.permissionTypeID === 'manageUsers'));
  } else if (filterType.value === 'noPermissions') {
    result = result.filter((u) => u.permissions.length === 0);
  } else if (filterType.value === 'facility' && filterFacilityID.value) {
    const facilityID = filterFacilityID.value;
    result = result.filter((u) => userCanAccessFacility(u, facilityID));
  } else if (filterType.value === 'facilityNonAdmin' && filterFacilityID.value) {
    const facilityID = filterFacilityID.value;
    result = result.filter(
      (u) => !u.permissions.some((p) => p.permissionTypeID === 'manageUsers') && userCanAccessFacility(u, facilityID),
    );
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (user) => user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
    );
  }

  return result;
});

const permissionAlreadyExists = computed(() => {
  if (!userEditing.value) return false;

  const adding = userPermissionAdding.value;
  return userEditing.value.permissions.some((permission) => {
    return (
      permission.permissionTypeID === adding.permissionTypeID &&
      permission.tenantID === adding.tenantID &&
      permission.facilityGroupID === adding.facilityGroupID &&
      permission.facilityID === adding.facilityID
    );
  });
});

const formatDate = (date: ISO8601Timestamp | null) => {
  if (!date) return 'Never';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const awaitingBackend = ref(false);

async function userCreate() {
  const { name, email, sendSetPasswordEmail, primaryTenantID, primaryFacilityGroupID, primaryFacilityID } =
    userNew.value;

  if (awaitingBackend.value || !name || !email) {
    return;
  }

  try {
    awaitingBackend.value = true;
    await crossAppActions.users.create({
      name,
      email: email as Email,
      primaryTenantID,
      primaryFacilityGroupID,
      primaryFacilityID,
    });
    users.value = await crossAppActions.users.listForManagement();
    searchQuery.value = email;
    userCreateSheetOpen.value = false;
    userNew.value = {
      name: '',
      email: '',
      sendSetPasswordEmail,
      primaryTenantID,
      primaryFacilityGroupID,
      primaryFacilityID,
    };

    if (sendSetPasswordEmail) {
      await passwordReset(email as Email);
    }
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to create user.',
      message: `We were unable to create that user. Error was: ${e.message}`,
    });
  } finally {
    awaitingBackend.value = false;
  }
}

function createPrimaryFacilityChanged() {
  if (userNew.value.primaryFacilityID) {
    const facility = facilitiesByFacilityID.value[userNew.value.primaryFacilityID];
    if (facility) {
      userNew.value.primaryTenantID = facility.tenantID;
      userNew.value.primaryFacilityGroupID = facility.facilityGroupID;
    }
  }
}

function createPrimaryGroupChanged() {
  if (userNew.value.primaryFacilityGroupID) {
    const group = groupsByGroupID.value[userNew.value.primaryFacilityGroupID];
    if (group) {
      userNew.value.primaryTenantID = group.tenantID;
      userNew.value.primaryFacilityID = null;
    }
  }
}

function createPrimaryTenantChanged() {
  if (userNew.value.primaryTenantID) {
    userNew.value.primaryFacilityGroupID = null;
    userNew.value.primaryFacilityID = null;
  }
}

function permissionFacilityChanged() {
  if (userPermissionAdding.value.facilityID) {
    const facility = facilitiesByFacilityID.value[userPermissionAdding.value.facilityID];
    if (facility) {
      userPermissionAdding.value.tenantID = facility.tenantID;
      userPermissionAdding.value.facilityGroupID = facility.facilityGroupID;
    }
  }
}

function permissionGroupChanged() {
  if (userPermissionAdding.value.facilityGroupID) {
    const group = groupsByGroupID.value[userPermissionAdding.value.facilityGroupID];
    if (group) {
      userPermissionAdding.value.tenantID = group.tenantID;
      userPermissionAdding.value.facilityID = null;
    }
  }
}

function permissionTenantChanged() {
  if (userPermissionAdding.value.tenantID) {
    userPermissionAdding.value.facilityGroupID = null;
    userPermissionAdding.value.facilityID = null;
  }
}

async function permissionAdd() {
  if (awaitingBackend.value || !userEditing.value) {
    return;
  }

  const userID = userEditing.value.userID;

  try {
    awaitingBackend.value = true;
    await crossAppActions.users.permissionAdd({
      userID,
      permissionTypeID: userPermissionAdding.value.permissionTypeID,
      tenantID: userPermissionAdding.value.tenantID,
      facilityGroupID: userPermissionAdding.value.facilityGroupID,
      facilityID: userPermissionAdding.value.facilityID,
    });
    users.value = await crossAppActions.users.listForManagement();
    userPermissionAdding.value = {
      permissionTypeID: 'updateFacility',
      tenantID: null,
      facilityGroupID: null,
      facilityID: null,
    };
    userEditing.value = users.value.find((u) => u.userID === userID) ?? null;
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to add permission.',
      message: `We were unable to add that permission. Error was: ${e.message}`,
    });
  } finally {
    awaitingBackend.value = false;
  }
}

async function permissionRemove(permissionID: PermissionID) {
  if (awaitingBackend.value || !userEditing.value) {
    return;
  }
  const userID = userEditing.value.userID;

  try {
    awaitingBackend.value = true;
    await crossAppActions.users.permissionRemove(permissionID);
    users.value = await crossAppActions.users.listForManagement();
    userEditing.value = users.value.find((u) => u.userID === userID) ?? null;
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to remove permission.',
      message: `We were unable to remove that permission. Error was: ${e.message}`,
    });
  } finally {
    awaitingBackend.value = false;
  }
}

function primaryFacilityChanged() {
  if (!userEditing.value) {
    return;
  }
  if (userEditing.value.primaryFacilityID) {
    const facility = facilitiesByFacilityID.value[userEditing.value.primaryFacilityID];
    if (facility) {
      userEditing.value.primaryTenantID = facility.tenantID;
      userEditing.value.primaryFacilityGroupID = facility.facilityGroupID;
    }
  }
}

function primaryGroupChanged() {
  if (!userEditing.value) {
    return;
  }
  if (userEditing.value.primaryFacilityGroupID) {
    const group = groupsByGroupID.value[userEditing.value.primaryFacilityGroupID];
    if (group) {
      userEditing.value.primaryTenantID = group.tenantID;
      userEditing.value.primaryFacilityID = null;
    }
  }
}

function primaryTenantChanged() {
  if (!userEditing.value) {
    return;
  }
  if (userEditing.value.primaryTenantID) {
    userEditing.value.primaryFacilityGroupID = null;
    userEditing.value.primaryFacilityID = null;
  }
}

async function userInfoSave() {
  if (awaitingBackend.value || !userEditing.value || !userEditing.value.name || !userEditing.value.email) {
    return;
  }
  const userID = userEditing.value.userID;

  try {
    awaitingBackend.value = true;
    await crossAppActions.users.userUpdate({
      userID,
      name: userEditing.value.name,
      email: userEditing.value.email,
      primaryTenantID: userEditing.value.primaryTenantID,
      primaryFacilityGroupID: userEditing.value.primaryFacilityGroupID,
      primaryFacilityID: userEditing.value.primaryFacilityID,
    });
    users.value = await crossAppActions.users.listForManagement();
    userEditing.value = users.value.find((u) => u.userID === userID) ?? null;
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to update user.',
      message: `We were unable to update that user. Error was: ${e.message}`,
    });
  } finally {
    awaitingBackend.value = false;
  }
}

const resetEmailsSentTo = ref(new Set<Email>());
async function passwordReset(email: Email) {
  try {
    awaitingBackend.value = true;
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin,
    });
    resetEmailsSentTo.value.add(email);
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to send email.',
      message: `We were unable to send an email to that user. Error was: ${e.message}`,
    });
  } finally {
    awaitingBackend.value = false;
  }
}
</script>

<template>
  <div class="w-full space-y-4">
    <Card>
      <CardHeader>
        <div class="flex items-center gap-4">
          <!-- Users Image on the left -->
          <div class="shrink-0">
            <div class="flex h-16 w-16 items-center justify-center rounded-full border-2 border-gray-200 bg-white p-2">
              <img :src="usersImg" alt="Users" class="h-10 w-10 object-contain" />
            </div>
          </div>
          <!-- Header text and actions -->
          <div class="flex w-full items-start justify-between">
            <div>
              <CardTitle class="text-2xl font-bold">Users</CardTitle>
              <CardDescription class="mt-1">Manage user access and roles</CardDescription>
            </div>
            <div>
              <Button @click="userCreateSheetOpen = true">
                <i class="fa-light fa-plus"></i>
                Create User
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <!-- Search & Filter -->
        <div class="mb-4 flex flex-col gap-2 sm:flex-row">
          <div class="relative flex-1">
            <i class="fa-light fa-magnifying-glass text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4"></i>
            <Input v-model="searchQuery" type="search" placeholder="Search users..." class="w-full pl-8" />
          </div>
          <select
            v-model="filterType"
            class="h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 sm:w-56"
          >
            <option value="all">All Users</option>
            <option value="admins">Admins</option>
            <option value="noPermissions">Users With No Permissions</option>
            <option value="facility">Users With Facility Access...</option>
            <option value="facilityNonAdmin">Non-Admins With Facility Access...</option>
          </select>
          <select
            v-if="filterType === 'facility' || filterType === 'facilityNonAdmin'"
            v-model="filterFacilityID"
            class="h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 sm:w-64"
          >
            <option :value="null">Select a facility...</option>
            <option v-for="facility of facilitiesSorted" :key="facility.facilityID" :value="facility.facilityID">
              {{ facility.name }}
            </option>
          </select>
        </div>

        <!-- Users Table -->
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Access</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead class="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <!-- Loading State -->
            <TableRow v-if="loading">
              <TableCell colspan="4">
                <div class="space-y-4 py-4">
                  <div v-for="i in 5" :key="i" class="flex items-center justify-between px-4">
                    <div class="flex items-center gap-4">
                      <Skeleton class="h-12 w-12 rounded-full" />
                      <div class="space-y-2">
                        <Skeleton class="h-4 w-[150px]" />
                        <Skeleton class="h-4 w-[100px]" />
                      </div>
                    </div>
                    <Skeleton class="h-8 w-[100px]" />
                  </div>
                </div>
              </TableCell>
            </TableRow>

            <!-- Empty State -->
            <TableRow v-else-if="usersFiltered.length === 0">
              <TableCell colspan="4" class="text-muted-foreground py-8 text-center"> No users found</TableCell>
            </TableRow>

            <!-- User Rows -->
            <TableRow v-for="user in usersFiltered" :key="user.userID" class="odd:bg-muted/50">
              <TableCell>
                <div class="flex items-center gap-3">
                  <div class="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
                    {{ user.name?.slice(0, 2).toUpperCase() || user.email.slice(0, 2).toUpperCase() }}
                  </div>
                  <div>
                    <div class="font-medium">{{ user.name || 'Unnamed User' }}</div>
                    <div class="text-muted-foreground text-sm">{{ user.email }}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>{{ userPermissionDescriptions[user.userID] }}</TableCell>
              <TableCell>{{ formatDate(user.lastLoginAt) }}</TableCell>
              <TableCell>
                <Button variant="outline" size="sm" @click="userEditing = jsonClone(user)">
                  Edit
                  <i class="fa-light fa-pen-to-square ml-2"></i>
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <Sheet v-model:open="userCreateSheetOpen">
      <SheetContent class="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Create User</SheetTitle>
          <SheetDescription>Provide the name and email address of the new user.</SheetDescription>
        </SheetHeader>
        <form @submit.prevent="userCreate()" class="mt-4 space-y-4">
          <Label class="block">
            <span class="mb-2 block">Name:</span>
            <Input type="text" v-model.trim="userNew.name" :disabled="awaitingBackend" required />
          </Label>

          <Label class="block">
            <span class="mb-2 block">Email:</span>
            <Input type="email" v-model.trim="userNew.email" :disabled="awaitingBackend" required />
          </Label>

          <Label class="flex items-center justify-start">
            <Checkbox class="me-2" v-model="userNew.sendSetPasswordEmail" :disabled="awaitingBackend" />
            <span>Send "Set Password" Email?</span>
          </Label>

          <Label class="block">
            <span class="mb-2 block">Primary Tenant:</span>
            <select
              v-model="userNew.primaryTenantID"
              @change="createPrimaryTenantChanged()"
              :disabled="awaitingBackend"
              class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option :value="null">None</option>
              <option v-for="tenant of tenantsByTenantID" :key="tenant.tenantID" :value="tenant.tenantID">
                {{ tenant.name }}
              </option>
            </select>
          </Label>

          <Label class="block">
            <span class="mb-2 block">Primary Facility Group:</span>
            <select
              v-model="userNew.primaryFacilityGroupID"
              @change="createPrimaryGroupChanged()"
              :disabled="awaitingBackend"
              class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option :value="null">None</option>
              <option v-for="group of groupsByGroupID" :key="group.facilityGroupID" :value="group.facilityGroupID">
                {{ group.name }}
              </option>
            </select>
          </Label>

          <Label class="block">
            <span class="mb-2 block">Primary Facility:</span>
            <select
              v-model="userNew.primaryFacilityID"
              @change="createPrimaryFacilityChanged()"
              :disabled="awaitingBackend"
              class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option :value="null">None</option>
              <option
                v-for="facility of facilitiesByFacilityID"
                :key="facility.facilityID"
                :value="facility.facilityID"
              >
                {{ facility.name }}
              </option>
            </select>
          </Label>

          <div class="text-end">
            <Button type="submit" variant="default" :disabled="awaitingBackend">
              <i class="fa-light fa-user-plus"></i>
              Create User
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>

    <Sheet v-model:open="userEditingSheetOpen">
      <SheetContent class="overflow-y-auto sm:max-w-4xl">
        <SheetHeader>
          <SheetTitle>Edit {{ userEditing?.name }} ({{ userEditing?.email }})</SheetTitle>
          <SheetDescription>Update this user's info and permissions.</SheetDescription>
        </SheetHeader>
        <template v-if="userEditing">
          <Table>
            <TableCaption class="caption-top">User Info</TableCaption>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableCell>
                <Input type="text" v-model.trim="userEditing.name" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableCell>
                <Input type="email" v-model.trim="userEditing.email" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableHead>Password</TableHead>
              <TableCell>
                <span v-if="resetEmailsSentTo.has(userEditing.email)" class="text-green-700">
                  <i class="fa-light fa-check me-2"></i>
                  Password reset email sent.
                </span>
                <Button
                  v-else
                  type="button"
                  variant="outline"
                  @click="passwordReset(userEditing.email)"
                  :disabled="awaitingBackend"
                >
                  <i class="fa-light fa-key"></i>
                  Send Password Reset Email
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableHead>Primary Tenant</TableHead>
              <TableCell>
                <select
                  v-model="userEditing.primaryTenantID"
                  @change="primaryTenantChanged()"
                  class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option :value="null">None</option>
                  <option v-for="tenant of tenantsByTenantID" :key="tenant.tenantID" :value="tenant.tenantID">
                    {{ tenant.name }}
                  </option>
                </select>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableHead>Primary Facility Group</TableHead>
              <TableCell>
                <select
                  v-model="userEditing.primaryFacilityGroupID"
                  @change="primaryGroupChanged()"
                  class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option :value="null">None</option>
                  <option v-for="group of groupsByGroupID" :key="group.facilityGroupID" :value="group.facilityGroupID">
                    {{ group.name }}
                  </option>
                </select>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableHead>Primary Facility</TableHead>
              <TableCell>
                <select
                  v-model="userEditing.primaryFacilityID"
                  @change="primaryFacilityChanged()"
                  class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option :value="null">None</option>
                  <option
                    v-for="facility of facilitiesByFacilityID"
                    :key="facility.facilityID"
                    :value="facility.facilityID"
                  >
                    {{ facility.name }}
                  </option>
                </select>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colspan="2" class="text-center">
                <Button type="button" variant="outline" :disabled="awaitingBackend" @click="userInfoSave()">
                  <i class="fa-light fa-floppy-disk"></i>
                  Update User Info
                </Button>
              </TableCell>
            </TableRow>
          </Table>
          <Table class="mt-8">
            <TableCaption class="caption-top">Permissions</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Permission Type</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Facility</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow v-for="permission of userEditing.permissions" :key="permission.permissionID">
                <TableCell>
                  {{
                    {
                      updateFacility: 'Update Facility',
                      manageUsers: 'Manage Users',
                      updateBaseFacts: 'Update Base Facts',
                      administerFacility: 'Administer Facility',
                    }[permission.permissionTypeID]
                  }}
                </TableCell>
                <TableCell>
                  {{ permission.tenantID ? tenantsByTenantID[permission.tenantID]?.name : 'All' }}
                </TableCell>
                <TableCell>
                  {{ permission.facilityGroupID ? groupsByGroupID[permission.facilityGroupID]?.name : 'All' }}
                </TableCell>
                <TableCell>
                  {{ permission.facilityID ? facilitiesByFacilityID[permission.facilityID]?.name : 'All' }}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    :disabled="awaitingBackend"
                    @click="permissionRemove(permission.permissionID)"
                  >
                    <i class="fa-light fa-trash"></i>
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colspan="5" class="text-center italic">Add new permission:</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <select
                    v-model="userPermissionAdding.permissionTypeID"
                    class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white file:border-0 file:bg-transparent placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="updateBaseFacts">Update Base Facts</option>
                    <option value="updateFacility">Update Facility</option>
                    <option value="administerFacility">Administer Facility</option>
                    <option value="manageUsers">Manage Users</option>
                  </select>
                </TableCell>
                <TableCell>
                  <select
                    v-model="userPermissionAdding.tenantID"
                    @change="permissionTenantChanged()"
                    class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white file:border-0 file:bg-transparent placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option :value="null">All</option>
                    <option v-for="tenant of tenantsByTenantID" :key="tenant.tenantID" :value="tenant.tenantID">
                      {{ tenant.name }}
                    </option>
                  </select>
                </TableCell>
                <TableCell>
                  <select
                    v-model="userPermissionAdding.facilityGroupID"
                    @change="permissionGroupChanged()"
                    class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white file:border-0 file:bg-transparent placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option :value="null">All</option>
                    <option
                      v-for="group of groupsByGroupID"
                      :key="group.facilityGroupID"
                      :value="group.facilityGroupID"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                </TableCell>
                <TableCell>
                  <select
                    v-model="userPermissionAdding.facilityID"
                    @change="permissionFacilityChanged()"
                    class="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-normal ring-offset-white file:border-0 file:bg-transparent placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option :value="null">All</option>
                    <option
                      v-for="facility of facilitiesByFacilityID"
                      :key="facility.facilityID"
                      :value="facility.facilityID"
                    >
                      {{ facility.name }}
                    </option>
                  </select>
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    :disabled="awaitingBackend || permissionAlreadyExists"
                    @click="permissionAdd()"
                  >
                    <i class="fa-light fa-plus"></i>
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Table class="mt-8">
            <TableCaption class="caption-top">Explanation of Permission Types</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Permission Type</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell class="font-medium">Update Base Facts</TableCell>
                <TableCell>Data Preparer</TableCell>
                <TableCell class="text-muted-foreground text-sm">
                  Can enter and edit product usage data and answer questionnaire items. Cannot access threshold review,
                  release review, Form R, or admin features.
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell class="font-medium">Update Facility</TableCell>
                <TableCell>Supervisor</TableCell>
                <TableCell class="text-muted-foreground text-sm">
                  All TRI workflow pages including threshold review, release review, and Form R final review, but cannot
                  submit the Form R or access admin features.
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell class="font-medium">Administer Facility</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell class="text-muted-foreground text-sm">
                  Full TRI workflow access including Form R submission, plus admin features such as release profiles,
                  chemicals, receptors, and usage import.
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell class="font-medium">Manage Users</TableCell>
                <TableCell>User Manager</TableCell>
                <TableCell class="text-muted-foreground text-sm">
                  Can access the Admin section and manage users (create, edit, assign permissions). Does not grant
                  access to TRI workflow pages on its own.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </template>
      </SheetContent>
    </Sheet>
  </div>
</template>
